import type { ConversationDetail } from '../../types/conversation';

type GetSelectedConversations = (() => ConversationDetail[]) | undefined;

/** Fallback only: virtualized columns keep just the visible cards in the DOM. */
export function readSelectedIdsFromDom(): string[]
{
  const selectedNodes = document.querySelectorAll('.dg-card-selected');
  const ids: string[] = [];
  selectedNodes.forEach(node =>
  {
    const id = node.getAttribute('data-conversationid');
    if (id) ids.push(id);
  });
  return ids;
}

/** Number of selected cards, from React state when available. */
export function getSelectedConversationCount(
  getSelectedConversations: GetSelectedConversations,
): number
{
  const fromState = getSelectedConversations?.();
  if (fromState) return fromState.length;
  return readSelectedIdsFromDom().length;
}

/** Ids of every selected card plus the current one, deduplicated. */
export function getSelectedConversationIds(
  getSelectedConversations: GetSelectedConversations,
  currentConversationId: number | string,
): string[]
{
  const fromState = getSelectedConversations?.() ?? [];
  const ids =
    fromState.length > 0
      ? fromState.map(c => String(c.ConversationId))
      : readSelectedIdsFromDom();

  const current = String(currentConversationId);
  if (!ids.includes(current)) ids.push(current);

  return [...new Set(ids)];
}

/** Id + title of every selected card plus the current one. */
export function getSelectedConversationTitles(
  getSelectedConversations: GetSelectedConversations,
  current: { ConversationId: number; Title?: string },
): Array<{ ConversationId: number; Title: string }>
{
  const byId = new Map<number, string>();

  for (const conv of getSelectedConversations?.() ?? [])
  {
    byId.set(conv.ConversationId, conv.Title ?? `#${conv.ConversationId}`);
  }

  if (!byId.has(current.ConversationId))
  {
    byId.set(current.ConversationId, current.Title ?? `#${current.ConversationId}`);
  }

  return [...byId].map(([ConversationId, Title]) => ({ ConversationId, Title }));
}
